import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  FeedbackError,
  SideBarModal,
  Skeleton,
  TextareaField,
} from '@/components';
import { showErrorToast } from '@/components/toast/globalToast';
import {
  ChecklistCatalog,
  ChecklistCatalogItem,
  getHandoverChecklistCatalog,
} from '@/services/api/handovers';
import {
  ChecklistCorrection,
  correctHandoverChecklist,
  CorrectHandoverChecklistResponse,
  getHandoverById,
  getHandoverChecklistCorrections,
  RecordedChecklistItem,
} from '@/services/api/opsHandovers';
import { RentalDetail } from '@/types/rental';
import { extractApiErrorMessage, openImageFileDialog } from '@/utils';
import { uploadHandoverPhoto } from '@/utils/handoverPhotoUpload';
import {
  describeChecklistChange,
  describeTimelineActor,
  formatDate,
} from '../rentalDisplay';

interface CorrectHandoverChecklistModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The correction landed; the parent owns the toast and the reload. */
  onCorrected: (result: CorrectHandoverChecklistResponse) => void;
  detail: RentalDetail;
}

// Matches the backend's limit: the reason is printed on the documents.
const REASON_MAX_LENGTH = 200;

interface Loaded {
  catalog: ChecklistCatalog;
  recorded: RecordedChecklistItem[];
  history: ChecklistCorrection[];
}

interface Photo {
  url: string;
  fileName: string;
}

/**
 * Fixes Ada / Tidak ada answers ops got wrong at pickup in fleet-app — the
 * unit had its charger, the checklist says it did not — and reissues the BAST
 * and the Perjanjian Sewa Kontrak that print them. Body panels are not
 * offered: they are photos, never an answer.
 */
const CorrectHandoverChecklistModal: React.FC<
  CorrectHandoverChecklistModalProps
> = ({ isOpen, onClose, onCorrected, detail }) => {
  const handoverID = detail.handover?.ID ?? '';
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Only the answers ops changed here; everything else reads as recorded.
  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  const [photos, setPhotos] = useState<Record<string, Photo>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!handoverID) return;
    setIsFetching(true);
    setLoadError(null);
    try {
      const [catalog, handover, history] = await Promise.all([
        getHandoverChecklistCatalog(),
        getHandoverById(handoverID),
        getHandoverChecklistCorrections(handoverID),
      ]);
      setLoaded({
        catalog,
        recorded: handover.handoverChecklist ?? [],
        history,
      });
    } catch (error) {
      setLoaded(null);
      setLoadError(extractApiErrorMessage(error, 'Coba muat ulang.'));
    } finally {
      setIsFetching(false);
    }
  }, [handoverID]);

  // Fresh on every open: another correction may have landed since.
  useEffect(() => {
    if (!isOpen) return;
    setAnswers({});
    setPhotos({});
    setUploading({});
    setReason('');
    setSubmitError(null);
    load();
  }, [isOpen, load]);

  const recordedByCode = useMemo(
    () => new Map((loaded?.recorded ?? []).map((item) => [item.code, item])),
    [loaded]
  );

  const groups = useMemo(
    () =>
      (loaded?.catalog.groups ?? [])
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => item.category === 'EQUIPMENT'),
        }))
        .filter((group) => group.items.length > 0),
    [loaded]
  );

  // A part the row never held reads as Tidak ada, as the documents print it.
  const recordedPresent = useCallback(
    (code: string) => recordedByCode.get(code)?.present ?? false,
    [recordedByCode]
  );

  const answerFor = (code: string) => answers[code] ?? recordedPresent(code);

  // An answer flipped back to what was recorded is no change at all.
  const changes = useMemo(
    () =>
      groups
        .flatMap((group) => group.items)
        .filter(
          (item) =>
            item.code in answers &&
            answers[item.code] !== recordedPresent(item.code)
        )
        .map((item) => ({
          code: item.code,
          label: item.label,
          present: answers[item.code],
        })),
    [groups, answers, recordedPresent]
  );
  const changedCodes = useMemo(
    () => new Set(changes.map((change) => change.code)),
    [changes]
  );

  const isUploading = Object.values(uploading).some(Boolean);
  const canSubmit =
    changes.length > 0 && !!reason.trim() && !isUploading && !isSubmitting;

  const setAnswer = (code: string, present: boolean) => {
    setAnswers((prev) => ({ ...prev, [code]: present }));
    // A photo only belongs to an answer flipped to Ada.
    if (!present || present === recordedPresent(code)) {
      setPhotos((prev) => {
        const next = { ...prev };
        delete next[code];
        return next;
      });
    }
  };

  const uploadPhoto = (code: string) => {
    openImageFileDialog(async (file) => {
      if (!file) return;
      setUploading((p) => ({ ...p, [code]: true }));
      try {
        const url = await uploadHandoverPhoto(file);
        setPhotos((p) => ({ ...p, [code]: { url, fileName: file.name } }));
      } catch {
        // The upload helper's own messages are English; this screen is not.
        showErrorToast('Error', 'Gagal mengunggah foto. Coba lagi.');
      } finally {
        setUploading((p) => ({ ...p, [code]: false }));
      }
    });
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const result = await correctHandoverChecklist(handoverID, {
        checklistItems: changes.map((change) => ({
          code: change.code,
          present: change.present,
          ...(photos[change.code] ? { photoUrl: photos[change.code].url } : {}),
        })),
        reason: reason.trim(),
      });
      onCorrected(result);
    } catch (error) {
      setSubmitError(
        extractApiErrorMessage(error, 'Koreksi gagal disimpan. Coba lagi.')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={onClose}
      position="center"
      width="md"
      header={
        <div>
          <h2 className="text-[18px] font-semibold text-gray-900">
            Koreksi Checklist Serah Terima
          </h2>
          <p className="text-[13px] text-gray-500 mt-0.5">
            {[detail.code, detail.vehicle?.platNomor]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
      }
      footer={
        <div className="flex flex-col gap-2">
          {submitError && (
            <p role="alert" className="text-[13px] text-red-600">
              {submitError}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="medium" onClick={onClose}>
              Batal
            </Button>
            <Button
              variant="primary"
              size="medium"
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              {isSubmitting ? 'Menyimpan...' : 'Simpan & Buat Ulang Dokumen'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-5 px-4 py-4 md:px-6 md:py-5">
        <div className="flex gap-2 rounded-lg bg-gray-50 px-3 py-2.5 text-[13px] text-gray-600">
          <i className="ri-information-line text-[16px] text-gray-400 leading-5"></i>
          <span>
            Ubah jawaban yang salah tercatat saat serah terima. Setelah
            disimpan, BAST dan Perjanjian Sewa Kontrak dibuat ulang. Tanda
            tangan tetap yang asli, dan baris yang dikoreksi diberi catatan
            siapa, kapan, dan alasannya. Mitra tidak dikirimi notifikasi.
          </span>
        </div>

        {isFetching ? (
          <div className="space-y-2" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-[56px]" rounded="md" />
            ))}
          </div>
        ) : loadError ? (
          <FeedbackError
            message="Gagal memuat checklist serah terima"
            description={loadError}
            onRetry={load}
          />
        ) : loaded ? (
          <>
            {groups.map((group) => (
              <section key={group.code}>
                <p className="mb-2 text-[13px] font-medium text-gray-500">
                  {group.title}
                </p>
                <div className="divide-y divide-gray-100 rounded-xl border border-gray-200">
                  {group.items.map((item) => (
                    <ChecklistRow
                      key={item.code}
                      item={item}
                      recorded={recordedByCode.get(item.code) ?? null}
                      present={answerFor(item.code)}
                      changed={changedCodes.has(item.code)}
                      photo={photos[item.code] ?? null}
                      isUploading={!!uploading[item.code]}
                      onSetPresent={(present) => setAnswer(item.code, present)}
                      onUpload={() => uploadPhoto(item.code)}
                    />
                  ))}
                </div>
              </section>
            ))}

            {changes.length > 0 && (
              <div className="rounded-xl bg-primary-50 px-4 py-3">
                <p className="text-[13px] font-semibold text-gray-900">
                  Perubahan ({changes.length})
                </p>
                <ul className="mt-1 space-y-0.5 text-[13px] text-gray-700">
                  {changes.map((change) => (
                    <li key={change.code}>{describeChecklistChange(change)}</li>
                  ))}
                </ul>
              </div>
            )}

            <section>
              <label
                htmlFor="checklist-correction-reason"
                className="mb-1.5 block text-[13px] font-medium text-gray-700"
              >
                Alasan koreksi <span className="text-red-500">*</span>
              </label>
              <TextareaField
                id="checklist-correction-reason"
                value={reason}
                onChange={setReason}
                rows={3}
                maxLength={REASON_MAX_LENGTH}
                placeholder="Contoh: charger ada di unit, terlewat dicentang saat serah terima"
              />
              <p className="mt-1 text-[12px] text-gray-500">
                Dicetak di dokumen, di samping baris yang dikoreksi.
              </p>
            </section>

            {loaded.history.length > 0 && (
              <section>
                <p className="mb-2 text-[13px] font-medium text-gray-500">
                  Koreksi sebelumnya
                </p>
                <ul className="space-y-2">
                  {loaded.history.map((entry) => (
                    <li
                      key={entry.ID}
                      className="rounded-lg border border-gray-200 px-3 py-2 text-[13px]"
                    >
                      <p className="text-gray-900">
                        {entry.changes.map(describeChecklistChange).join(', ')}
                      </p>
                      <p className="mt-0.5 text-gray-500">
                        {[
                          formatDate(entry.createdAt, ''),
                          describeTimelineActor(entry.createdBy),
                          entry.reason,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        ) : null}
      </div>
    </SideBarModal>
  );
};

interface ChecklistRowProps {
  item: ChecklistCatalogItem;
  recorded: RecordedChecklistItem | null;
  present: boolean;
  changed: boolean;
  photo: Photo | null;
  isUploading: boolean;
  onSetPresent: (present: boolean) => void;
  onUpload: () => void;
}

/** "Tercatat: …" — what fleet-app stored, before anything here changes it. */
const recordedLabel = (recorded: RecordedChecklistItem | null): string => {
  if (!recorded || recorded.captured === false) return 'belum dinilai';
  return recorded.present ? 'Ada' : 'Tidak ada';
};

const ChecklistRow: React.FC<ChecklistRowProps> = ({
  item,
  recorded,
  present,
  changed,
  photo,
  isUploading,
  onSetPresent,
  onUpload,
}) => {
  return (
    <div className={`px-4 py-3 ${changed ? 'bg-primary-50' : ''}`}>
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[14px] font-medium text-gray-900">
              {item.label}
            </span>
            {changed && (
              <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-medium text-primary-700">
                Diubah
              </span>
            )}
          </div>
          <p className="text-[12px] text-gray-500">
            Tercatat: {recordedLabel(recorded)}
          </p>
        </div>
        <div
          role="radiogroup"
          aria-label={item.label}
          className="inline-flex shrink-0 overflow-hidden rounded-md border border-gray-200 text-[12px] font-semibold"
        >
          <button
            type="button"
            role="radio"
            aria-checked={present}
            onClick={() => onSetPresent(true)}
            className={`px-3 py-1 ${
              present ? 'bg-primary-600 text-white' : 'bg-white text-gray-600'
            }`}
          >
            Ada
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={!present}
            onClick={() => onSetPresent(false)}
            className={`border-l border-gray-200 px-3 py-1 ${
              !present ? 'bg-gray-500 text-white' : 'bg-white text-gray-600'
            }`}
          >
            Tidak ada
          </button>
        </div>
      </div>

      {changed && present && (
        <div className="mt-2">
          {isUploading ? (
            <span className="text-[12px] text-gray-500">Mengunggah…</span>
          ) : photo ? (
            <div className="flex items-center gap-3">
              <span className="max-w-[200px] truncate text-[12px] font-medium text-green-600">
                {photo.fileName}
              </span>
              <button
                type="button"
                onClick={onUpload}
                className="text-[12px] font-semibold text-primary-600"
              >
                Ganti
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={onUpload}
              className="inline-flex items-center gap-1 rounded-md border border-primary-100 bg-white px-3 py-1.5 text-[12px] font-semibold text-primary-600"
            >
              <i className="ri-camera-line"></i>
              Foto (opsional)
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CorrectHandoverChecklistModal;
